/* Rewind header behaviour - Cuts-style.
   Three jobs: hover/focus the mega panel, toggle the mobile drawer, and run
   the drawer accordions. Nothing here owns the cart or search: the cart pill
   is Dawn's #cart-icon-bubble (cart-drawer.js binds to it) and the search chip
   is Dawn's <details-modal>, so both keep working untouched. */
(function () {
  'use strict';

  var OPEN_DELAY = 60;
  var CLOSE_DELAY = 180;

  function initMega(header) {
    var items = header.querySelectorAll('.rw-nav__item[data-mega]');
    if (!items.length) return;
    var timer = null;

    function closeAll() {
      items.forEach(function (li) {
        li.setAttribute('data-open', 'false');
        var trigger = li.querySelector('.rw-nav__link');
        if (trigger && trigger.tagName === 'BUTTON') trigger.setAttribute('aria-expanded', 'false');
      });
      header.setAttribute('data-mega-open', 'false');
    }

    function open(li) {
      closeAll();
      li.setAttribute('data-open', 'true');
      var trigger = li.querySelector('.rw-nav__link');
      if (trigger && trigger.tagName === 'BUTTON') trigger.setAttribute('aria-expanded', 'true');
      header.setAttribute('data-mega-open', 'true');
    }

    items.forEach(function (li) {
      li.addEventListener('mouseenter', function () {
        clearTimeout(timer);
        timer = setTimeout(function () {
          open(li);
        }, OPEN_DELAY);
      });
      li.addEventListener('mouseleave', function () {
        clearTimeout(timer);
        timer = setTimeout(closeAll, CLOSE_DELAY);
      });
      li.addEventListener('focusin', function () {
        clearTimeout(timer);
        open(li);
      });
      li.addEventListener('focusout', function (event) {
        if (!li.contains(event.relatedTarget)) closeAll();
      });
      // keyboard and touch: the top-level item toggles rather than navigates
      var trigger = li.querySelector('.rw-nav__link');
      if (trigger && trigger.tagName === 'BUTTON') {
        trigger.addEventListener('click', function () {
          if (li.getAttribute('data-open') === 'true') closeAll();
          else open(li);
        });
      }
    });

    header.addEventListener('keyup', function (event) {
      if (event.key === 'Escape') closeAll();
    });
  }

  function initDrawer(header) {
    var burger = header.querySelector('.rw-burger');
    var drawer = header.querySelector('.rw-drawer');
    if (!burger || !drawer) return;

    function setTop() {
      var bar = header.querySelector('.rw-header__bar');
      if (!bar) return;
      var rect = bar.getBoundingClientRect();
      drawer.style.setProperty('--rwh-drawer-top', Math.max(0, rect.bottom) + 'px');
    }

    function setOpen(open) {
      burger.setAttribute('aria-expanded', open ? 'true' : 'false');
      burger.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      drawer.setAttribute('data-open', open ? 'true' : 'false');
      // a transparent banner would be white-on-white over the open drawer
      header.setAttribute('data-drawer-open', open ? 'true' : 'false');
      document.body.classList.toggle('rw-no-scroll', open);
      if (open) setTop();
    }

    burger.addEventListener('click', function () {
      setOpen(burger.getAttribute('aria-expanded') !== 'true');
    });

    document.addEventListener('keyup', function (event) {
      if (event.key === 'Escape' && drawer.getAttribute('data-open') === 'true') setOpen(false);
    });

    window.addEventListener('resize', function () {
      if (drawer.getAttribute('data-open') === 'true') setTop();
      if (window.innerWidth >= 990 && drawer.getAttribute('data-open') === 'true') setOpen(false);
    });

    window.addEventListener('scroll', function () {
      if (drawer.getAttribute('data-open') === 'true') setTop();
    }, { passive: true });
  }

  function initAccordions(header) {
    header.querySelectorAll('.rw-acc__trigger').forEach(function (trigger) {
      var panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!panel) return;

      trigger.addEventListener('click', function () {
        var open = trigger.getAttribute('aria-expanded') === 'true';
        if (open) {
          panel.style.height = panel.scrollHeight + 'px';
          requestAnimationFrame(function () {
            panel.style.height = '0px';
            panel.style.opacity = '0';
          });
        } else {
          panel.style.height = panel.scrollHeight + 'px';
          panel.style.opacity = '1';
          panel.addEventListener('transitionend', function once(event) {
            if (event.propertyName !== 'height') return;
            panel.removeEventListener('transitionend', once);
            if (trigger.getAttribute('aria-expanded') === 'true') panel.style.height = 'auto';
          });
        }
        trigger.setAttribute('aria-expanded', open ? 'false' : 'true');
      });
    });
  }

  /* The overlay banner: transparent while the page is at the top, pinned and
     solid once it scrolls.

     Watched with an IntersectionObserver on a 1px sentinel sitting at the
     header's natural position, NOT with a scroll listener - this storefront
     does not fire scroll events on window (apps rebind it), so a scroll-based
     version pinned itself open and never came back. The observer only fires on
     the crossing, so it is cheaper too. */
  /* How much is pinned above the header - the announcement bar, which stays put
     too. Measured rather than configured, so changing the bar's height in the
     editor needs no second setting. */
  function stickOffset(header) {
    var total = 0;
    var node = header.closest('.shopify-section-group-header-group');
    node = node ? node.previousElementSibling : null;
    while (node) {
      if (node.classList && node.classList.contains('shopify-section-group-header-group')) {
        if (getComputedStyle(node).position === 'sticky') total += node.offsetHeight;
      }
      node = node.previousElementSibling;
    }
    return total;
  }

  // set on the section WRAPPER, because that is the element made sticky; the
  // header inside inherits the variable for its own fixed state
  function applyStickOffset(header) {
    var offset = stickOffset(header);
    var target = header.closest('.shopify-section-group-header-group') || header;
    target.style.setProperty('--rwh-stick-top', offset + 'px');
    return offset;
  }

  function initOverlay(header) {
    applyStickOffset(header);
    window.addEventListener('resize', function () {
      applyStickOffset(header);
    });

    if (header.getAttribute('data-overlay') !== 'true') return;
    if (header.dataset.solidOnScroll !== 'true') return;

    var offset = stickOffset(header);
    var sentinel = header.parentElement.querySelector('.rw-sentinel');

    function set(stuck) {
      var want = stuck ? 'true' : 'false';
      if (header.getAttribute('data-stuck') !== want) header.setAttribute('data-stuck', want);
    }

    if (sentinel && typeof IntersectionObserver !== 'undefined') {
      // the crossing happens at the BOTTOM of the pinned announcement bar,
      // not at the top of the viewport
      new IntersectionObserver(
        function (entries) {
          set(!entries[0].isIntersecting);
        },
        { threshold: 0, rootMargin: '-' + offset + 'px 0px 0px 0px' }
      ).observe(sentinel);
    }

    // Belt and braces. Some apps rebind scrolling to a container, so listen on
    // both, and re-check when the tab comes back - a backgrounded tab delivers
    // neither observer callbacks nor scroll events, and would come back stale.
    function check() {
      if (sentinel) set(sentinel.getBoundingClientRect().bottom <= offset);
      else set((window.scrollY || document.documentElement.scrollTop || 0) > 4);
    }

    window.addEventListener('scroll', check, { passive: true });
    document.addEventListener('scroll', check, { passive: true });
    document.addEventListener('visibilitychange', check);
    window.addEventListener('resize', check);
    check();
  }

  function init(root) {
    root.querySelectorAll('.rw-header').forEach(function (header) {
      if (header.dataset.rwInit === 'true') return;
      header.dataset.rwInit = 'true';
      initMega(header);
      initDrawer(header);
      initAccordions(header);
      initOverlay(header);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // the theme editor re-renders the section on every setting change
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
